import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper,
  Button,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Stack,
  Avatar,
  Chip
} from '@mui/material';
import { 
  VerifiedUser as VerifiedUserIcon,
  Psychology as PsychologyIcon,
  Gavel as GavelIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';

const AboutPage: React.FC = () => {
  const values = [
    {
      icon: <VerifiedUserIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Trust & Integrity',
      description: 'We build trust through transparency, honesty, and delivering on our promises.'
    },
    {
      icon: <PsychologyIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Innovation',
      description: 'We continuously innovate to provide cutting-edge solutions for legal professionals.'
    },
    {
      icon: <GavelIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Legal Excellence',
      description: 'We are committed to maintaining the highest standards of legal accuracy and compliance.'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Security',
      description: 'We protect your sensitive data with enterprise-grade security measures.'
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Efficiency',
      description: 'We help legal professionals work smarter and faster without compromising quality.'
    },
    {
      icon: <PublicIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Accessibility',
      description: 'We make advanced legal technology accessible to organizations of all sizes.'
    }
  ];

  const teamMembers = [
    {
      name: 'Sarah Johnson',
      title: 'CEO & Co-Founder',
      image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2787&q=80',
      bio: 'Former corporate attorney with 15+ years of experience at top law firms. Sarah founded Contract AI to transform the way legal professionals work with documents.'
    },
    {
      name: 'Michael Chen',
      title: 'CTO & Co-Founder',
      image: 'https://images.unsplash.com/photo-1556157382-97eda2d62296?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2670&q=80',
      bio: 'AI researcher and engineer with a PhD in Machine Learning. Michael leads our technical team in developing cutting-edge AI solutions for legal document analysis.'
    },
    {
      name: 'Emily Rodriguez',
      title: 'Chief Legal Officer',
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2688&q=80',
      bio: 'Former General Counsel with expertise in contract law and regulatory compliance. Emily ensures our platform meets the highest legal standards.'
    },
    {
      name: 'David Wilson',
      title: 'VP of Product',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2787&q=80',
      bio: 'Product leader with 10+ years of experience building legal tech solutions. David oversees our product strategy and roadmap.'
    },
    {
      name: 'Jennifer Lee',
      title: 'VP of Customer Success',
      image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2861&q=80',
      bio: 'Customer experience expert with a background in legal operations. Jennifer leads our customer success team, ensuring clients get maximum value from our platform.'
    },
    {
      name: 'Robert Thompson',
      title: 'VP of Sales',
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2787&q=80',
      bio: 'Sales leader with extensive experience in enterprise SaaS. Robert drives our growth strategy and builds relationships with key clients.'
    }
  ];

  const milestones = [
    {
      year: '2018',
      title: 'Founded in San Francisco',
      description: 'Contract AI was founded by Sarah Johnson and Michael Chen with a mission to transform legal document workflows.'
    },
    {
      year: '2019',
      title: 'Seed Funding',
      description: 'Raised $3M in seed funding from leading legal tech investors to develop our core AI technology.'
    },
    {
      year: '2020',
      title: 'Beta Launch',
      description: 'Released our beta platform to select law firms and corporate legal departments for testing and feedback.'
    },
    {
      year: '2021',
      title: 'Series A Funding',
      description: 'Secured $12M in Series A funding to accelerate product development and expand our team.'
    },
    {
      year: '2022',
      title: 'Enterprise Launch',
      description: 'Officially launched our enterprise platform with advanced AI capabilities for contract analysis and generation.'
    },
    {
      year: '2023',
      title: 'Global Expansion',
      description: 'Expanded operations to Europe and Asia, serving clients in over 20 countries worldwide.'
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ bgcolor: '#1a365d', color: 'white', py: 8, pt: 4 }}>
        {/* Header */}
        <LandingHeader />
        
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h2" component="h1" fontWeight="bold" gutterBottom>
                About Contract AI
              </Typography>
              <Typography variant="h6" sx={{ maxWidth: '700px', mb: 4, opacity: 0.9 }}>
                We're on a mission to transform legal document workflows with artificial intelligence
              </Typography>
              <Button 
                variant="contained" 
                color="primary"
                size="large"
                href="/contact"
                sx={{ 
                  bgcolor: 'white', 
                  color: '#1a365d',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                  py: 1.5,
                  px: 4,
                  borderRadius: 2
                }}
              >
                Get in Touch
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2047&q=80"
                alt="Contract AI Team"
                sx={{ 
                  width: '100%',
                  borderRadius: 3,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* Our Story */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box 
              component="img"
              src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2670&q=80"
              alt="Our Story"
              sx={{ 
                width: '100%',
                borderRadius: 3,
                boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              Our Story
            </Typography>
            <Typography variant="body1" paragraph>
              Contract AI was founded in 2018 by Sarah Johnson, a former corporate attorney, and Michael Chen, an AI researcher, who recognized the inefficiencies in traditional legal document workflows.
            </Typography>
            <Typography variant="body1" paragraph>
              After spending countless hours reviewing and drafting contracts, Sarah knew there had to be a better way. She partnered with Michael, who was developing cutting-edge natural language processing technology, to create a solution that would transform how legal professionals work with documents.
            </Typography>
            <Typography variant="body1" paragraph>
              What started as a small team in San Francisco has grown into a global company serving thousands of legal professionals across law firms, corporate legal departments, and businesses of all sizes. Our AI-powered platform has helped clients save millions of hours on contract work while improving accuracy and reducing risk.
            </Typography>
            <Typography variant="body1">
              Today, Contract AI continues to innovate at the intersection of legal expertise and artificial intelligence, with a mission to make advanced legal technology accessible to organizations worldwide.
            </Typography>
          </Grid>
        </Grid>
      </Container>
      
      {/* Our Values */}
      <Box sx={{ bgcolor: '#f8faff', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom textAlign="center">
            Our Values
          </Typography>
          <Typography variant="body1" paragraph textAlign="center" sx={{ maxWidth: '700px', mx: 'auto', mb: 6 }}>
            These core values guide everything we do at Contract AI
          </Typography>
          
          <Grid container spacing={3}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper 
                  sx={{ 
                    p: 4, 
                    height: '100%',
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: '0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    {value.icon}
                  </Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {value.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {value.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      
      {/* Our Team */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom textAlign="center">
          Our Leadership Team
        </Typography>
        <Typography variant="body1" paragraph textAlign="center" sx={{ maxWidth: '700px', mx: 'auto', mb: 6 }}>
          Meet the experts behind Contract AI
        </Typography>
        
        <Grid container spacing={4}>
          {teamMembers.map((member, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card 
                sx={{ 
                  height: '100%',
                  borderRadius: 2,
                  overflow: 'hidden',
                  transition: '0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <CardMedia
                  component="img"
                  height="300"
                  image={member.image}
                  alt={member.name}
                />
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {member.name}
                  </Typography>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    {member.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {member.bio}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
      
      {/* Company Timeline */}
      <Box sx={{ bgcolor: '#f8faff', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom textAlign="center">
            Our Journey
          </Typography>
          <Typography variant="body1" paragraph textAlign="center" sx={{ maxWidth: '700px', mx: 'auto', mb: 6 }}>
            Key milestones in our company history
          </Typography>
          
          <Box sx={{ position: 'relative' }}>
            {/* Timeline line */}
            <Box 
              sx={{ 
                position: 'absolute', 
                left: { xs: '20px', md: '50%' }, 
                transform: { xs: 'none', md: 'translateX(-1px)' },
                width: '2px', 
                height: '100%', 
                bgcolor: 'rgba(0,0,0,0.1)',
                zIndex: 0
              }} 
            />
            
            {/* Timeline items */}
            <Stack spacing={6}>
              {milestones.map((milestone, index) => (
                <Box 
                  key={index} 
                  sx={{ 
                    position: 'relative',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: index % 2 === 0 ? 'row' : 'row-reverse' },
                    alignItems: { xs: 'flex-start', md: 'center' },
                    justifyContent: 'space-between',
                    ml: { xs: 5, md: 0 }
                  }}
                >
                  {/* Timeline dot */}
                  <Box 
                    sx={{ 
                      position: { xs: 'absolute', md: 'static' },
                      left: { xs: -40, md: 'auto' },
                      width: 20, 
                      height: 20, 
                      borderRadius: '50%', 
                      bgcolor: 'primary.main',
                      border: '4px solid white',
                      boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.3)',
                      zIndex: 1,
                      mx: { md: 3 }
                    }} 
                  />
                  
                  {/* Content */}
                  <Paper 
                    sx={{ 
                      p: 3, 
                      borderRadius: 2,
                      width: { xs: '100%', md: 'calc(50% - 30px)' },
                      boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                    }}
                  >
                    <Chip 
                      label={milestone.year} 
                      color="primary" 
                      sx={{ mb: 1, fontWeight: 'bold' }} 
                    />
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {milestone.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {milestone.description}
                    </Typography>
                  </Paper>
                </Box>
              ))}
            </Stack>
          </Box>
        </Container>
      </Box>
      
      {/* CTA Section */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box 
          sx={{ 
            textAlign: 'center',
            p: { xs: 4, md: 6 },
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #1a365d 0%, #2d8cff 100%)',
            color: 'white',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
          }}
        >
          <Typography 
            variant="h4" 
            fontWeight="bold" 
            gutterBottom
          >
            Join Our Team
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 4, 
              maxWidth: '600px', 
              mx: 'auto',
              opacity: 0.9
            }}
          >
            We're always looking for talented individuals who are passionate about transforming the legal industry with AI technology.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button 
              variant="contained" 
              size="large"
              href="/careers"
              sx={{ 
                bgcolor: 'white',
                color: '#1a365d',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: '8px',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)'
                }
              }}
            >
              View Open Positions
            </Button>
            <Button 
              variant="outlined" 
              size="large"
              href="/contact"
              sx={{ 
                borderColor: 'white',
                color: 'white',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: '8px',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Contact Us
            </Button>
          </Box>
        </Box>
      </Container>
      
      <Footer />
    </Box>
  );
};

export default AboutPage; 